import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Stack,
  Button,
  Divider,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Pagination,
  Autocomplete,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useLocation, useParams } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get, post, put, del } from '../utils/api';

// US states mapping (name shown, code stored)
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

const RELATION_OPTIONS = ['self', 'spouse', 'son', 'daughter', 'father', 'mother', 'sibling', 'other'];
const OCCASION_TYPES = ['birthday', 'marriage_anniversary', 'other'];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function formatRelationLabel(value) {
  if (!value) return '—';
  const v = String(value).toLowerCase();
  const map = { self: 'Self', spouse: 'Spouse', son: 'Son', daughter: 'Daughter', father: 'Father', mother: 'Mother', sibling: 'Sibling', other: 'Other' };
  return map[v] || value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
}
function formatOccasionTypeLabel(type, customLabel) {
  if (!type) return customLabel || '—';
  const t = String(type).toLowerCase();
  if (t === 'other' && customLabel && String(customLabel).trim()) return String(customLabel).trim();
  const map = { birthday: 'Birthday', marriage_anniversary: 'Marriage anniversary', other: 'Other' };
  return map[t] || type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
}
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const DetailRow = ({ label, value, editable = true, onEdit }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mr: 2, minWidth: 140 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1 }}>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || '-'}</Typography>
    </Box>
    {editable && (
      <Button size="small" variant="outlined" onClick={onEdit} sx={{ ml: 2 }}>
        Edit
      </Button>
    )}
  </Box>
);

// Red circular X close button (macOS-style), larger with white icon
const CloseX = ({ onClick, title = 'Close' }) => (
  <Box
    onClick={onClick}
    title={title}
    role="button"
    aria-label="Close"
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      backgroundColor: '#e53935',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      '&:hover': { filter: 'brightness(0.95)' },
    }}
  >
    {CloseIcon ? <CloseIcon sx={{ fontSize: 18, color: '#fff' }} /> : 'X'}
  </Box>
);

const FilterButton = ({ active, children, onClick }) => (
  <Button
    size="small"
    variant={active ? 'contained' : 'outlined'}
    color={active ? 'primary' : 'inherit'}
    onClick={onClick}
  >
    {children}
  </Button>
);

const Stat = ({ label, value, help }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
      {help && (
        <Typography variant="caption" color="text.secondary">{help}</Typography>
      )}
    </CardContent>
  </Card>
);

const DevoteeDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const user = location.state?.user || {};
  // Local profile state for display and edits
  const [profile, setProfile] = React.useState({
    first_name: user.first_name || user.billing?.first_name || '',
    last_name: user.last_name || user.billing?.last_name || '',
    email: user.email || user.billing?.email || '',
    phone: (user.phone || user.billing?.phone || '').toString(),
    billing: {
      address_1: user.billing?.address_1 || user.address?.address1 || '',
      address_2: user.billing?.address_2 || user.address?.address2 || '',
      city: user.billing?.city || user.address?.city || '',
      state: user.billing?.state || user.address?.state || '',
      postcode: user.billing?.postcode || user.address?.zip || '',
      country: user.billing?.country || user.address?.country || 'US',
    },
    meta: {
      spouse: user.meta?.spouse || '',
      family_members: Array.isArray(user.meta?.family_members) ? user.meta.family_members : [],
      gothram: user.meta?.gothram || '',
      occasions_email_reminders: user.meta?.occasions_email_reminders !== false && user.meta?.occasions_email_reminders !== 'false',
    }
  });
  const displayId = user.customer_id || user.id || id;

  const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Devotee Name';
  const email = profile.email || 'email@domain.com';
  const phone = profile.phone || '(000) 000-0000';
  const address1 = profile.billing?.address_1 || '';
  const address2 = profile.billing?.address_2 || '';
  const city = profile.billing?.city || '';
  const state = profile.billing?.state || '';
  const zip = profile.billing?.postcode || '';
  const country = profile.billing?.country || '';

  const addrLine1 = [address1, address2].filter(Boolean).join(', ');
  const addrLine2 = `${city}${city&&state?', ':''}${state} ${zip}`.trim();
  const fullAddress = [addrLine1, addrLine2, country].filter(Boolean).join('\n');

  const [range, setRange] = React.useState('1y'); // default last 12 months
  const [orders, setOrders] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [ordersError, setOrdersError] = React.useState('');
  const [ordersMeta, setOrdersMeta] = React.useState(null);
  const [orderEditOpen, setOrderEditOpen] = React.useState(false);
  const [orderToEdit, setOrderToEdit] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [orderPage, setOrderPage] = React.useState(1);
  const pageSize = 25;
  const [years, setYears] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [productQuery, setProductQuery] = React.useState('');
  const [productOptions, setProductOptions] = React.useState([]);
  const [productLoading, setProductLoading] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [customStart, setCustomStart] = React.useState(''); // yyyy-mm-dd
  const [customEnd, setCustomEnd] = React.useState('');     // yyyy-mm-dd
  const [pledgesOpen, setPledgesOpen] = React.useState(false);
  const [pledgesLoading, setPledgesLoading] = React.useState(false);
  const [pledgesError, setPledgesError] = React.useState('');
  const [pledges, setPledges] = React.useState([]); // pledge-pending and pledge-completed orders
  const [selectedPledge, setSelectedPledge] = React.useState(null);
  const [pledgeTxns, setPledgeTxns] = React.useState([]);
  const [stmtOpen, setStmtOpen] = React.useState(false);
  const [stmtYear, setStmtYear] = React.useState(new Date().getFullYear());
  const [stmtLoading, setStmtLoading] = React.useState(false);
  const [stmtError, setStmtError] = React.useState('');
  const [stmtOrders, setStmtOrders] = React.useState([]);
  const [stmtEmailSending, setStmtEmailSending] = React.useState(false);
  const [stmtEmailStatus, setStmtEmailStatus] = React.useState({ type: '', message: '' });
  // Page-level details loading from local DB
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  // Family members & occasions
  const [familyMembers, setFamilyMembers] = React.useState([]);
  const [familyLoading, setFamilyLoading] = React.useState(false);
  const [familyError, setFamilyError] = React.useState('');
  const [occasions, setOccasions] = React.useState([]);
  const [occasionsLoading, setOccasionsLoading] = React.useState(false);
  const [occasionsError, setOccasionsError] = React.useState('');
  const [familyDialogOpen, setFamilyDialogOpen] = React.useState(false);
  const [familyForm, setFamilyForm] = React.useState({ id: null, name: '', relation: 'spouse', gender: '' });
  const [familyFormSaving, setFamilyFormSaving] = React.useState(false);
  const [familyFormError, setFamilyFormError] = React.useState('');
  const [occasionDialogOpen, setOccasionDialogOpen] = React.useState(false);
  const [occasionForm, setOccasionForm] = React.useState({
    id: null,
    family_member_id: null,
    occasion_type: 'birthday',
    occasion_label: '',
    day: new Date().getDate(),
    month: new Date().getMonth() + 1,
  });
  const [occasionFormSaving, setOccasionFormSaving] = React.useState(false);
  const [occasionFormError, setOccasionFormError] = React.useState('');
  const [occasionsReminderSaving, setOccasionsReminderSaving] = React.useState(false);
  // Edit dialog state
  const [editOpen, setEditOpen] = React.useState(false);
  const [editLoading, setEditLoading] = React.useState(false);
  const [editError, setEditError] = React.useState('');
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');
  const [deleteSuccess, setDeleteSuccess] = React.useState('');
  const [hasCompletedOrder, setHasCompletedOrder] = React.useState(null); // null = unknown, true/false after check
  const [editForm, setEditForm] = React.useState({
    first_name: '', last_name: '', phone: '', spouse: '', family_members: '', gothram: '',
    address_1: '', address_2: '', city: '', state: '', postcode: '', country: 'US'
  });
  const openEdit = React.useCallback(() => {
    setEditError('');
    // Seed form from current on-screen profile for instant population
    setEditForm({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      phone: profile.phone || '',
      spouse: profile.meta?.spouse || '',
      family_members: (profile.meta?.family_members || []).join(', '),
      gothram: profile.meta?.gothram || '',
      address_1: profile.billing?.address_1 || '',
      address_2: profile.billing?.address_2 || '',
      city: profile.billing?.city || '',
      state: profile.billing?.state || '',
      postcode: profile.billing?.postcode || '',
      country: profile.billing?.country || 'US'
    });
    setEditOpen(true);
  }, [profile]);

  // Check if devotee has at least one completed order (hide Delete button if so)
  React.useEffect(() => {
    const customerId = user.customer_id || user.id || id;
    if (!customerId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await get('/ldb/orders', { customer_id: customerId, status: 'completed', per_page: 1 });
        if (cancelled) return;
        const total = resp?.meta?.total ?? 0;
        setHasCompletedOrder(total > 0);
      } catch {
        if (!cancelled) setHasCompletedOrder(false); // on error allow showing button; backend will still block if orders exist
      }
    })();
    return () => { cancelled = true; };
  }, [id, user.customer_id, user.id]);

  // Fetch customer from Local DB to populate personal details
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!displayId) return;
        setDetailsLoading(true);
        const data = await get(`/ldb/customers/${encodeURIComponent(displayId)}`);
        if (cancelled) return;
        // Map local DB row to profile shape
        setProfile(prev => ({
          ...prev,
          first_name: data.first_name || data.billing_first_name || prev.first_name,
          last_name: data.last_name || data.billing_last_name || prev.last_name,
          email: data.email || data.billing_email || prev.email,
          phone: data.phone || data.billing_phone || prev.phone,
          billing: {
            ...prev.billing,
            address_1: data.billing_address_1 || prev.billing.address_1,
            address_2: data.billing_address_2 || prev.billing.address_2,
            city: data.billing_city || prev.billing.city,
            state: data.billing_state || prev.billing.state,
            postcode: data.billing_postcode || prev.billing.postcode,
            country: data.billing_country || prev.billing.country,
          },
          meta: {
            ...prev.meta,
            // meta_data is jsonb map, try common keys
            spouse: data.meta_data?.billing_spouse || prev.meta.spouse,
            gothram: data.meta_data?.billing_gothram || prev.meta.gothram,
            family_members: (() => {
              const fm = data.meta_data?.billing_family_members || '';
              if (Array.isArray(fm)) return fm;
              return String(fm||'').split(',').map(s=>s.trim()).filter(Boolean);
            })(),
            occasions_email_reminders:
              data.meta_data?.occasions_email_reminders !== false
              && data.meta_data?.occasions_email_reminders !== 'false',
          }
        }));
      } catch (e) {
        console.warn('Failed to fetch local customer profile:', e?.message || e);
      } finally { if (!cancelled) setDetailsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [displayId]);

  React.useEffect(() => {
    if (!location.state?.orderEditSaved) return;
    setReloadKey((k) => k + 1);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state?.orderEditSaved]);

  const loadFamilyAndOccasions = React.useCallback(async () => {
    if (!displayId) return;
    try {
      setFamilyLoading(true);
      setFamilyError('');
      const famRes = await get(`/ldb/devotees/${encodeURIComponent(displayId)}/family-members`);
      const data = Array.isArray(famRes?.data?.data) ? famRes.data.data : Array.isArray(famRes?.data) ? famRes.data : [];
      setFamilyMembers(data);
    } catch (e) {
      console.warn('[Devotee][Family] Failed to load family members', e?.message || e);
      setFamilyMembers([]);
    } finally {
      setFamilyLoading(false);
    }
    try {
      setOccasionsLoading(true);
      setOccasionsError('');
      const occRes = await get(`/ldb/devotees/${encodeURIComponent(displayId)}/family-occasions`);
      const data = Array.isArray(occRes?.data?.data) ? occRes.data.data : Array.isArray(occRes?.data) ? occRes.data : [];
      const flat = [];
      data.forEach((m) => {
        (m.occasions || []).forEach((o) => {
          flat.push({
            ...o,
            family_member_id: o.family_member_id ?? m.id,
            member: { id: m.id, name: m.name, relation: m.relation },
          });
        });
      });
      setOccasions(flat);
    } catch (e) {
      console.error('[Devotee][Occasions] Failed to load occasions', e);
      setOccasionsError('Failed to load occasions.');
    } finally {
      setOccasionsLoading(false);
    }
  }, [displayId]);

  React.useEffect(() => {
    if (displayId) loadFamilyAndOccasions();
  }, [displayId, loadFamilyAndOccasions]);

  const saveOccasionsEmailReminder = async (checked) => {
    if (!displayId) return;
    setOccasionsReminderSaving(true);
    try {
      await put(`/ldb/customers/${encodeURIComponent(displayId)}`, { occasions_email_reminders: !!checked });
      setProfile((prev) => ({
        ...prev,
        meta: { ...prev.meta, occasions_email_reminders: !!checked },
      }));
    } catch (e) {
      console.error('[Devotee][OccasionsReminder]', e?.message || e);
    } finally {
      setOccasionsReminderSaving(false);
    }
  };

  const openFamilyAdd = () => {
    setFamilyForm({ id: null, name: '', relation: 'spouse', gender: '' });
    setFamilyFormError('');
    setFamilyDialogOpen(true);
  };
  const openFamilyEdit = (m) => {
    setFamilyForm({
      id: m.id,
      name: m.name || '',
      relation: m.relation || 'spouse',
      gender: m.gender || '',
    });
    setFamilyFormError('');
    setFamilyDialogOpen(true);
  };
  const saveFamily = async () => {
    const name = String(familyForm.name || '').trim();
    const relation = String(familyForm.relation || '').trim();
    if (!name) {
      setFamilyFormError('Name is required.');
      return;
    }
    if (!relation) {
      setFamilyFormError('Relation is required.');
      return;
    }
    setFamilyFormSaving(true);
    setFamilyFormError('');
    try {
      if (familyForm.id) {
        await put(`/ldb/devotees/${displayId}/family-members/${familyForm.id}`, {
          name,
          relation,
          gender: String(familyForm.gender || '').trim(),
        });
      } else {
        if (familyMembers.length >= 10) {
          setFamilyFormError('You can add up to 10 family members.');
          return;
        }
        await post(`/ldb/devotees/${displayId}/family-members`, {
          name,
          relation,
          gender: String(familyForm.gender || '').trim(),
        });
      }
      setFamilyDialogOpen(false);
      loadFamilyAndOccasions();
    } catch (e) {
      setFamilyFormError(e?.response?.data?.error || e?.message || 'Failed to save.');
    } finally {
      setFamilyFormSaving(false);
    }
  };
  const deleteFamilyMember = async (m) => {
    if (m.is_primary) return;
    if (!window.confirm(`Remove family member "${m.name || 'Unnamed'}"?`)) return;
    try {
      await del(`/ldb/devotees/${displayId}/family-members/${m.id}`);
      loadFamilyAndOccasions();
    } catch (e) {
      setFamilyError(e?.response?.data?.error || e?.message || 'Failed to delete.');
    }
  };

  const openOccasionAdd = () => {
    setOccasionForm({
      id: null,
      family_member_id: familyMembers[0]?.id ?? null,
      occasion_type: 'birthday',
      occasion_label: '',
      day: new Date().getDate(),
      month: new Date().getMonth() + 1,
    });
    setOccasionFormError('');
    setOccasionDialogOpen(true);
  };
  const openOccasionEdit = (o) => {
    setOccasionForm({
      id: o.id,
      family_member_id: o.family_member_id ?? o.member?.id ?? null,
      occasion_type: o.occasion_type || 'birthday',
      occasion_label: o.occasion_label || '',
      day: o.day ?? 1,
      month: o.month ?? 1,
    });
    setOccasionFormError('');
    setOccasionDialogOpen(true);
  };
  const saveOccasion = async () => {
    const memberId = occasionForm.family_member_id;
    const occasion_type = String(occasionForm.occasion_type || '').trim();
    const day = parseInt(occasionForm.day, 10);
    const month = parseInt(occasionForm.month, 10);
    if (!memberId) {
      setOccasionFormError('Please select a family member.');
      return;
    }
    if (!occasion_type) {
      setOccasionFormError('Occasion type is required.');
      return;
    }
    if (!day || day < 1 || day > 31) {
      setOccasionFormError('Day must be 1–31.');
      return;
    }
    if (!month || month < 1 || month > 12) {
      setOccasionFormError('Month must be 1–12.');
      return;
    }
    setOccasionFormSaving(true);
    setOccasionFormError('');
    try {
      if (occasionForm.id) {
        await put(
          `/ldb/devotees/${displayId}/family-members/${memberId}/occasions/${occasionForm.id}`,
          {
            occasion_type,
            occasion_label: String(occasionForm.occasion_label || '').trim(),
            day,
            month,
          }
        );
      } else {
        if (occasions.length >= 3) {
          setOccasionFormError('You can add up to 3 important occasions.');
          return;
        }
        await post(`/ldb/devotees/${displayId}/family-members/${memberId}/occasions`, {
          occasion_type,
          occasion_label: String(occasionForm.occasion_label || '').trim(),
          day,
          month,
        });
      }
      setOccasionDialogOpen(false);
      loadFamilyAndOccasions();
    } catch (e) {
      setOccasionFormError(e?.response?.data?.error || e?.message || 'Failed to save.');
    } finally {
      setOccasionFormSaving(false);
    }
  };
  const deleteOccasionRow = async (o) => {
    const memberId = o.family_member_id ?? o.member?.id;
    if (!memberId) return;
    if (!window.confirm('Remove this occasion?')) return;
    try {
      await del(`/ldb/devotees/${displayId}/family-members/${memberId}/occasions/${o.id}`);
      loadFamilyAndOccasions();
    } catch (e) {
      setOccasionsError(e?.response?.data?.error || e?.message || 'Failed to delete.');
    }
  };

  // Load local customer on edit open (no network save; purely UI-side for now)
  React.useEffect(() => {
    let cancelled = false;
    const loadCustomer = async () => {
      if (!editOpen) return;
      try {
        setEditLoading(true); setEditError('');
        const customerId = displayId;
        const data = await get(`/ldb/customers/${encodeURIComponent(customerId)}`);
        if (cancelled) return;
        // Extract fields from local row
        const billing = {
          address_1: data.billing_address_1 || '',
          address_2: data.billing_address_2 || '',
          city: data.billing_city || '',
          state: data.billing_state || '',
          postcode: data.billing_postcode || '',
          country: data.billing_country || 'US',
          phone: data.billing_phone || data.phone || ''
        };
        const meta = data.meta_data || {};
        let fm = meta.billing_family_members || '';
        if (Array.isArray(fm)) fm = fm.join(', ');
        else fm = String(fm||'');
        const form = {
          first_name: data.first_name || data.billing_first_name || '',
          last_name: data.last_name || data.billing_last_name || '',
          phone: billing.phone || '',
          spouse: meta.billing_spouse || '',
          family_members: fm || '',
          gothram: meta.billing_gothram || '',
          address_1: billing.address_1,
          address_2: billing.address_2,
          city: billing.city,
          state: billing.state,
          postcode: billing.postcode,
          country: billing.country,
        };
        setEditForm(form);
        // Update profile preview to reflect latest from server
        setProfile(prev => ({
          ...prev,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          billing: {
            ...prev.billing,
            address_1: form.address_1,
            address_2: form.address_2,
            city: form.city,
            state: form.state,
            postcode: form.postcode,
            country: form.country,
          },
          meta: {
            ...prev.meta,
            spouse: form.spouse,
            gothram: form.gothram,
            family_members: (form.family_members || '').split(',').map(s => s.trim()).filter(Boolean)
          }
        }));
      } catch (e) {
        if (!cancelled) setEditError(e?.message || 'Failed to load customer');
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    };
    loadCustomer();
    return () => { cancelled = true; };
  }, [editOpen, displayId]);

  const handleEditChange = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));
  const handleAddressChange = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));
  const handleDeleteDevotee = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError('');
      setDeleteSuccess('');
      await del(`/devotees/${encodeURIComponent(displayId)}`);
      setDeleteSuccess('Devotee deleted successfully.');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to delete devotee';
      setDeleteError(msg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditSave = async () => {
  try {
    setEditLoading(true); setEditError('');
    const famArray = (editForm.family_members || '').split(',').map(s => s.trim()).filter(Boolean);
    const customerId = displayId;
    console.log('handleEditSave start', { customerId, editForm });

    // 1) Update Woo first
    const wooPayload = {
      first_name: editForm.first_name,
      last_name: editForm.last_name,
      billing: {
        address_1: editForm.address_1,
        address_2: editForm.address_2,
        city: editForm.city,
        state: editForm.state,
        postcode: editForm.postcode,
        country: editForm.country,
        phone: editForm.phone,
      },
      spouse: editForm.spouse,
      gothram: editForm.gothram,
      family_members: famArray,
    };
    console.log('Devotee Save -> PUT /devotees (Woo first)', { id: customerId, wooPayload });
    const wooUpdated = await put(`/devotees/${encodeURIComponent(customerId)}?minimal=true`, wooPayload);

    if (wooUpdated && typeof wooUpdated === 'object') {
      setProfile(prev => ({
        ...prev,
        first_name: wooUpdated.first_name ?? prev.first_name,
        last_name: wooUpdated.last_name ?? prev.last_name,
        email: wooUpdated.email ?? prev.email,
        phone: wooUpdated.phone ?? prev.phone,
        billing: {
          ...prev.billing,
          address_1: wooUpdated.billing?.address_1 ?? prev.billing.address_1,
          address_2: wooUpdated.billing?.address_2 ?? prev.billing.address_2,
          city: wooUpdated.billing?.city ?? prev.billing.city,
          state: wooUpdated.billing?.state ?? prev.billing.state,
          postcode: wooUpdated.billing?.postcode ?? prev.billing.postcode,
          country: wooUpdated.billing?.country ?? prev.billing.country,
        },
        meta: {
          ...prev.meta,
          spouse: wooUpdated.meta?.spouse ?? prev.meta.spouse,
          gothram: wooUpdated.meta?.gothram ?? prev.meta.gothram,
          family_members: Array.isArray(wooUpdated.meta?.family_members) ? wooUpdated.meta.family_members : (prev.meta.family_members || []),
        }
      }));
    }

    // 2) Persist to local DB after Woo success
    const ldbPayload = {
      first_name: wooUpdated?.first_name ?? editForm.first_name,
      last_name: wooUpdated?.last_name ?? editForm.last_name,
      phone: wooUpdated?.phone ?? editForm.phone,
      billing: {
        address_1: wooUpdated?.billing?.address_1 ?? editForm.address_1,
        address_2: wooUpdated?.billing?.address_2 ?? editForm.address_2,
        city: wooUpdated?.billing?.city ?? editForm.city,
        state: wooUpdated?.billing?.state ?? editForm.state,
        postcode: wooUpdated?.billing?.postcode ?? editForm.postcode,
        country: wooUpdated?.billing?.country ?? editForm.country,
        phone: wooUpdated?.billing?.phone ?? editForm.phone,
      },
      spouse: wooUpdated?.meta?.spouse ?? editForm.spouse,
      gothram: wooUpdated?.meta?.gothram ?? editForm.gothram,
      family_members: Array.isArray(wooUpdated?.meta?.family_members) ? wooUpdated.meta.family_members : famArray,
    };
    console.log('Devotee Save -> PUT /ldb/customers (after Woo)', { id: customerId, ldbPayload });
    await put(`/ldb/customers/${encodeURIComponent(customerId)}`, ldbPayload);

    setEditOpen(false);
  } catch (e) {
    setEditError(e?.message || 'Save failed');
  } finally {
    setEditLoading(false);
  }
};
  // Stats (completed-only)
  const [statsLoading, setStatsLoading] = React.useState(false);
  const [lifetimeTotal, setLifetimeTotal] = React.useState(null);
  const [ytdTotal, setYtdTotal] = React.useState(null);
  const [lastYearTotal, setLastYearTotal] = React.useState(null);
  const [threeYearsTotal, setThreeYearsTotal] = React.useState(null);
  const [lifetimeCount, setLifetimeCount] = React.useState(0);

  const periodTotal = React.useMemo(() => {
    try {
      const completed = (orders || []).filter(o => String(o.status||'').toLowerCase()==='completed');
      const sum = completed.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(sum);
    } catch {
      const completed = (orders || []).filter(o => String(o.status||'').toLowerCase()==='completed');
      const sum = completed.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return `$${sum.toFixed(2)}`;
    }
  }, [orders]);

  // Lifetime average (formatted)
  const lifetimeAvgFmt = React.useMemo(() => {
    try {
      if (!lifetimeCount) return '$0.00';
      const currency = (orders?.[0]?.currency || 'USD');
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((Number(lifetimeTotal || 0)) / lifetimeCount);
    } catch {
      if (!lifetimeCount) return '$0.00';
      const avg = (Number(lifetimeTotal || 0)) / lifetimeCount;
      return `$${avg.toFixed(2)}`;
    }
  }, [lifetimeTotal, lifetimeCount, orders]);

  const periodAvg = React.useMemo(() => {
    try {
      const completed = (orders || []).filter(o => String(o.status||'').toLowerCase()==='completed');
      if (!completed.length) return '$0.00';
      const sum = completed.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(sum / completed.length);
    } catch {
      const completed = (orders || []).filter(o => String(o.status||'').toLowerCase()==='completed');
      if (!completed.length) return '$0.00';
      const sum = completed.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);
      return `$${(sum / completed.length).toFixed(2)}`;
    }
  }, [orders]);

  // Helper: sum completed orders over a range with pagination (date-only)
  const sumCompleted = React.useCallback(async ({ range = 'all', start, end }) => {
    const customerId = user.customer_id || user.id || id;
    if (!customerId) return 0;
    const per_page = 100;
    let page = 1;
    let total = 0;
    while (true) {
      const params = { customer_id: customerId, status: 'completed', page, per_page };
      if (range === 'custom') { params.start = start; params.end = end; }
      const resp = await get('/ldb/orders', params);
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      total += batch.reduce((s, o) => s + (Number(o.total_num)||0), 0);
      if (batch.length < per_page) break;
      page += 1;
      if (page > 50) break;
    }
    return total;
  }, [id, user.customer_id, user.id]);

  // Helper: sum and count completed orders
  const sumCompletedWithCount = React.useCallback(async ({ range = 'all', start, end }) => {
    const customerId = user.customer_id || user.id || id;
    if (!customerId) return { total: 0, count: 0 };
    const per_page = 100;
    let page = 1;
    let total = 0;
    let count = 0;
    while (true) {
      const params = { customer_id: customerId, status: 'completed', page, per_page };
      if (range === 'custom') { params.start = start; params.end = end; }
      const resp = await get('/ldb/orders', params);
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      total += batch.reduce((s, o) => s + (Number(o.total_num)||0), 0);
      count += batch.length;
      if (batch.length < per_page) break;
      page += 1;
      if (page > 50) break;
    }
    return { total, count };
  }, [id, user.customer_id, user.id]);

  // Load lifetime, YTD, prior calendar year, and 3-years stats
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const customerId = user.customer_id || user.id || id;
      if (!customerId) return;
      setStatsLoading(true);
      try {
        const now = new Date();
        const year = now.getFullYear();
        const startYtd = `${year}-01-01`;
        const endYtd = now.toISOString().slice(0,10);
        const priorYear = year - 1;
        const startLastYear = `${priorYear}-01-01`;
        const endLastYear = `${priorYear}-12-31`;
        const start3 = `${year-2}-01-01`;
        const end3 = endYtd;
        const [{ total: lifeTotal, count: lifeCount }, ytd, lastYear, three] = await Promise.all([
          sumCompletedWithCount({ range: 'all' }),
          sumCompleted({ range: 'custom', start: startYtd, end: endYtd }),
          sumCompleted({ range: 'custom', start: startLastYear, end: endLastYear }),
          sumCompleted({ range: 'custom', start: start3, end: end3 })
        ]);
        if (!cancelled) {
          setLifetimeTotal(lifeTotal);
          setLifetimeCount(lifeCount);
          setYtdTotal(ytd);
          setLastYearTotal(lastYear);
          setThreeYearsTotal(three);
        }
      } catch (e) {
        if (!cancelled) {
          setLifetimeTotal(0); setYtdTotal(0); setLastYearTotal(0); setThreeYearsTotal(0);
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, user.customer_id, user.id, sumCompleted, sumCompletedWithCount]);

  const closeStatementDialog = React.useCallback(() => {
    setStmtOpen(false);
    setStmtEmailStatus({ type: '', message: '' });
    setStmtEmailSending(false);
  }, []);

  // Build email-friendly HTML with tables (more reliable across email clients)
  const buildEmailStatementHtml = React.useCallback(() => {
    const year = stmtYear;
    const rows = [...stmtOrders].sort((a,b) => new Date(b.date_created||b.date_created_gmt) - new Date(a.date_created||a.date_created_gmt));
    const currency = rows[0]?.currency || 'USD';
    const fmt = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(n||0));
    const total = rows.reduce((s,o) => s + (parseFloat(o.total)||0), 0);

    const style = `
      <style>
        body { font-family: Arial, Helvetica, sans-serif; color: #222; }
        .muted { color: #666; }
        .header-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
        .header-table td { vertical-align: top; width: 50%; padding: 4px 6px; }
        .greeting { margin: 10px 0; }
        .table { width: 100%; border-collapse: collapse; }
        .table th, .table td { padding: 6px 8px; border-bottom: 1px solid #e0e0e0; text-align: left; }
        .table th.right, .table td.right { text-align: right; }
        .title { margin: 10px 0 6px 0; font-weight: bold; }
        .total { text-align: right; font-weight: bold; margin-top: 8px; }
      </style>`;

    const headerHtml = `
      <table class="header-table">
        <tr>
          <td>
            <div class="muted">For Year ${year}</div>
            <div><strong>Name:</strong> ${name}</div>
            <div><strong>Email:</strong> ${email}</div>
            <div><strong>Phone:</strong> ${phone}</div>
            ${fullAddress ? `<div><strong>Address:</strong> ${fullAddress.split('\n').join(', ')}</div>` : ''}
          </td>
          <td>
            <div class="muted">Statement Provided By</div>
            <div><strong>India Cultural Center and Temple (ICCT)</strong></div>
            <div>12005 Highway 64, Eads, TN 38028</div>
            <div>Phone: (901) 202-6070  ·  Email: info@icctmemphis.org</div>
            <div>Website: www.icctmemphis.org  ·  Tax ID: 58-1522313</div>
          </td>
        </tr>
      </table>`;

    const greetingHtml = `
      <div class="greeting">
        <div>Namaste ${name},</div>
        <div class="muted">Thank you for your generous support. This is your contribution statement for the year ${year}. You may use this statement for tax purposes as permitted by IRS guidelines. ICCT provides this statement as a courtesy and is not responsible for any IRS inquiries related to misuse or interpretation of this information.</div>
        <div class="muted" style="margin-top:6px;">With gratitude,<br/>India Cultural Center and Temple (ICCT)</div>
      </div>`;

    const tableHeader = `
      <div class="title">Contributions in ${year}</div>
      <table class="table">
        <thead>
          <tr>
            <th style="width:10%">Seq #</th>
            <th style="width:20%">Date</th>
            <th style="width:20%">Status</th>
            <th>Contribution</th>
            <th class="right" style="width:15%">Total</th>
          </tr>
        </thead>
        <tbody>`;

    const tableRows = rows.map((o, idx) => {
      const date = new Date(o.date_created || o.date_created_gmt).toLocaleDateString();
      const status = String(o.status || '').charAt(0).toUpperCase() + String(o.status || '').slice(1);
      const items = Array.isArray(o.line_items) ? o.line_items : [];
      const fees = Array.isArray(o.fee_lines) ? o.fee_lines : [];
      const contributionLines = [
        ...items.map(li => {
          const qty = li.quantity && Number(li.quantity) > 1 ? ` ×${Number(li.quantity)}` : '';
          return `${li.name || 'Item'}${qty}`;
        }),
        ...fees.map(f => f.name || '').filter(Boolean),
      ];
      const contribHtml = contributionLines.length > 0
        ? contributionLines.map(l => `<div>${l}</div>`).join('')
        : '—';
      return `<tr>
        <td>${idx + 1}</td>
        <td>${date}</td>
        <td>${status}</td>
        <td>${contribHtml}</td>
        <td class="right">${fmt(o.total)}</td>
      </tr>`;
    }).join('');

    const tableFooter = `</tbody></table>
      <div class="total">Total: ${fmt(total)}</div>
      <div class="muted" style="margin-top:8px; font-size: 12px;">
        India Cultural Center and Temple (ICCT) is a registered 501(c)(3) non-profit organization. Donations are tax-deductible as allowed by IRS regulations. Tax ID: 58-1522313
      </div>`;

    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>${style}</head><body>
      <h2 style="margin:0 0 6px 0;">Contribution Statement</h2>
      ${headerHtml}
      ${greetingHtml}
      ${tableHeader}${tableRows}${tableFooter}
    </body></html>`;
  }, [stmtOrders, stmtYear, name, email, phone, fullAddress]);

  // Helper to fetch all orders for a year (paginate over /api/orders/by-customer)
  const loadYearOrders = React.useCallback(async (customerId, year) => {
    const start = `${year}-01-01`;
    const end = `${year}-12-31`;
    const per_page = 100;
    let page = 1;
    let all = [];
    // Loop until no more pages; fetch from local DB and enrich with items for product names
    while (true) {
      const resp = await get('/ldb/orders', { customer_id: customerId, start: `${start}T00:00:00.000Z`, end: `${end}T23:59:59.999Z`, status: 'completed', page, per_page });
      const batch = Array.isArray(resp?.data) ? resp.data : [];
      // Enrich each order with items (for product name in statement)
      const detailed = await Promise.all(batch.map(async (o) => {
        try {
          const det = await get(`/ldb/orders/${o.id}`);
          const line_items = Array.isArray(det?.items) ? det.items.map(it => ({
            id: it.item_id || it.id,
            name: it.name,
            product_id: it.product_id,
            variation_id: it.variation_id,
            total: String(it.total_num ?? 0)
          })) : [];
          return {
            id: o.id,
            status: o.status,
            currency: o.currency || 'USD',
            total: String(o.total_num || 0),
            date_created: o.date_created_gmt || o.date_created,
            line_items
          };
        } catch { return { id: o.id, status: o.status, currency: o.currency||'USD', total: String(o.total_num||0), date_created: o.date_created_gmt||o.date_created, line_items: [] }; }
      }));
      all = all.concat(detailed);
      const hasMoreByCount = batch.length === per_page; // if exactly full page, assume more
      if (!hasMoreByCount) break;
      page += 1;
    }
    return all;
  }, []);

  // Auto-load statement data when dialog opens
  React.useEffect(() => {
    const customerId = user.customer_id || user.id || id;
    if (!stmtOpen || !customerId) return;
    let cancelled = false;
    (async () => {
      setStmtLoading(true); setStmtError(''); setStmtOrders([]);
      try {
        const all = await loadYearOrders(customerId, stmtYear);
        if (!cancelled) setStmtOrders(all);
      } catch (err) {
        if (!cancelled) setStmtError(err?.message || 'Failed to load statement');
      } finally {
        if (!cancelled) setStmtLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stmtOpen, stmtYear, id, user.customer_id, user.id, loadYearOrders]);

  // Load pledges via centralized pledge-totals API (same paid/balance logic as Pledges page and Kotilinga dialogs; no 100-order cap)
  React.useEffect(() => {
    const customerId = user.customer_id || user.id || id;
    if (!pledgesOpen || !customerId) return;
    let cancelled = false;
    const load = async () => {
      setPledgesLoading(true); setPledgesError(''); setSelectedPledge(null); setPledgeTxns([]);
      try {
        const res = await get('/ldb/pledge-totals/by-customer', { customer_id: customerId });
        if (cancelled) return;
        const data = Array.isArray(res?.data) ? res.data : [];
        setPledges(data);
      } catch (e) {
        if (!cancelled) setPledgesError(e?.message || 'Failed to load pledges');
      } finally {
        if (!cancelled) setPledgesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [pledgesOpen, id, user.customer_id, user.id]);

  const periodCount = React.useMemo(() => (orders ? orders.length : 0), [orders]);

  // Fetch orders when id/range changes (from local DB)
  React.useEffect(() => {
    const customerId = user.customer_id || user.id || id;
    if (!customerId) return;
    if (range === 'custom' && (!customStart || !customEnd)) {
      // Wait until both dates selected for custom range
      return;
    }

    let cancelled = false;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      setOrdersError('');
      try {
        const params = { customer_id: customerId };
        if (range === 'custom') {
          params.start = new Date(`${customStart}T00:00:00.000Z`).toISOString();
          params.end = new Date(`${customEnd}T23:59:59.999Z`).toISOString();
        }
        // Apply year filter if chosen (overrides range to custom)
        if (selectedYear) {
          params.start = new Date(`${selectedYear}-01-01T00:00:00.000Z`).toISOString();
          params.end = new Date(`${selectedYear}-12-31T23:59:59.999Z`).toISOString();
        } else if (range === 'custom') {
          params.start = new Date(`${customStart}T00:00:00.000Z`).toISOString();
          params.end = new Date(`${customEnd}T23:59:59.999Z`).toISOString();
        }
        // Determine which endpoint to use based on product search
        const hasProductSearch = (selectedProduct && selectedProduct.id) || (productQuery && productQuery.trim().length >= 3);
        let endpoint = '/ldb/orders';
        
        if (hasProductSearch) {
          // Use search endpoint for product filtering
          endpoint = '/ldb/orders/search';
          // Product search spans all time; ignore date params when searching
          delete params.start; delete params.end;
          if (selectedProduct && selectedProduct.id) {
            params.product_ids = String(selectedProduct.id);
          } else {
            params.product = productQuery.trim();
          }
        }
        
        // Add pagination parameters
        params.page = orderPage;
        params.per_page = pageSize;
        const res = await get(endpoint, params);
        if (!cancelled) {
          const arr = Array.isArray(res?.data) ? res.data : [];
          const mapped = arr.map(o => ({
            id: o.id,
            customer_id: o.customer_id,
            status: o.status,
            currency: o.currency || 'USD',
            total: String(o.total_num || 0),
            date_created: o.date_created_gmt || o.date_created,
            billing_first_name: o.billing_first_name,
            billing_last_name: o.billing_last_name,
            billing_email: o.billing_email
          }));
          // Enrich each order with its line items (for product display)
          const detailed = await Promise.all(mapped.map(async (o) => {
            try {
              const det = await get(`/ldb/orders/${o.id}`);
              const line_items = Array.isArray(det?.items) ? det.items.map(it => ({
                id: it.item_id || it.id,
                name: it.name,
                product_id: it.product_id,
                variation_id: it.variation_id,
                quantity: it.quantity,
                total: String(it.total_num ?? it.price_num ?? 0)
              })) : [];
              return { ...o, line_items };
            } catch {
              return { ...o, line_items: [] };
            }
          }));
          // Build variation map (variation_id -> label from attributes) for all products present
          try {
            const productIds = Array.from(new Set(detailed.flatMap(ord => (ord.line_items || []).map(li => li.product_id)).filter(Boolean)));
            let variationLabelById = new Map();
            if (productIds.length > 0) {
              const resVars = await get('/ldb/facets/variations', { product_id: productIds.join(',') });
              const vars = Array.isArray(resVars?.variations) ? resVars.variations : [];
              variationLabelById = new Map(vars.map(v => {
                const attrs = Array.isArray(v.attributes) ? v.attributes : [];
                // Join attribute names/options to a compact label
                const label = attrs.map(a => {
                  const name = (a.name || a.attribute || '').toString();
                  const val = (a.option || a.value || '').toString();
                  return name && val ? `${name}: ${val}` : (val || name);
                }).filter(Boolean).join(', ');
                return [v.id, label || (v.sku || '')];
              }));
            }
            // Attach variation_label to each line item
            for (const ord of detailed) {
              if (Array.isArray(ord.line_items)) {
                ord.line_items = ord.line_items.map(li => ({
                  ...li,
                  variation_label: variationLabelById.get(li.variation_id) || ''
                }));
              }
            }
          } catch {}
          setOrders(detailed);
          setOrdersMeta({ total: res?.meta?.total || detailed.length });
        }
      } catch (e) {
        if (!cancelled) setOrdersError(e?.message || 'Failed to load orders');
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    };
    fetchOrders();

    return () => { cancelled = true; };
  }, [id, user.customer_id, user.id, range, customStart, customEnd, reloadKey, selectedYear, selectedProduct, productQuery, orderPage]);

  // Build years dynamically based on customer's orders (any status)
  React.useEffect(() => {
    const customerId = user.customer_id || user.id || id;
    if (!customerId) return;
    let cancelled = false;
    (async () => {
      try {
        let page = 1; const per_page = 100; const yrs = new Set();
        while (page <= 5) { // cap pages for performance
          const resp = await get('/ldb/orders', { customer_id: customerId, page, per_page });
          const batch = Array.isArray(resp?.data) ? resp.data : [];
          for (const o of batch) {
            const d = o.date_created_gmt || o.date_created;
            if (d) yrs.add(String(new Date(d).getFullYear()));
          }
          if (batch.length < per_page) break;
          page += 1;
        }
        if (!cancelled) setYears(Array.from(yrs).sort((a,b)=> Number(b)-Number(a)));
      } catch { if (!cancelled) setYears([]); }
    })();
    return () => { cancelled = true; };
  }, [id, user.customer_id, user.id]);

  // Product autocomplete options (faceted products)
  const loadProductOptions = React.useCallback(async (q) => {
    if (!q || q.trim().length < 3) { setProductOptions([]); return; }
    try { setProductLoading(true); const res = await get('/ldb/facets/products', { q }); setProductOptions(Array.isArray(res?.products) ? res.products : []); }
    catch { setProductOptions([]); } finally { setProductLoading(false); }
  }, []);

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      {/* Blocking loading dialog while fetching devotee details */}
      <Dialog open={detailsLoading} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CloseX onClick={() => { /* block closing while loading */ }} />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={28} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Loading devotee details...</Typography>
              <Typography variant="body2" color="text.secondary">Please wait while we retrieve the latest info</Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>Devotee Details</Typography>
        <Chip label={`ID: ${displayId ?? '-'}`} size="small" />
      </Stack>

      <Grid container spacing={2} direction="column">
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" onClick={openEdit}>Edit Details</Button>
                  {hasCompletedOrder === false && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setDeleteError('');
                        setDeleteSuccess('');
                        setDeleteOpen(true);
                      }}
                    >
                      Delete Devotee
                    </Button>
                  )}
                </Stack>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                <Box sx={{ display: 'flex' }}>
                  <Card variant="outlined" sx={{ flex: 1, height: '100%' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <DetailRow label="Name" value={name} editable={false} />
                        <DetailRow label="Email" value={email} editable={false} />
                        <DetailRow label="Phone" value={phone} editable={false} />
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <Card variant="outlined" sx={{ flex: 1, height: '100%' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <DetailRow label="Spouse" value={profile.meta?.spouse || '—'} editable={false} />
                        <DetailRow label="Family Members (legacy notes)" value={(profile.meta?.family_members || []).join(', ') || '—'} editable={false} />
                        <DetailRow label="Gothram" value={profile.meta?.gothram || '—'} editable={false} />
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ display: 'flex' }}>
                  <Card variant="outlined" sx={{ flex: 1, height: '100%' }}>
                    <CardContent>
                      <Stack spacing={1.25}>
                        <DetailRow label="Address" value={<Box component="span" sx={{ whiteSpace: 'pre-line' }}>{fullAddress}</Box>} editable={false} />
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {/* Family members & occasions — strict 50/50 with CSS Grid */}
              <Box
                sx={{
                  mt: 3,
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2,
                  '& > *': { minWidth: 0 },
                }}
              >
                <Card variant="outlined" sx={{ height: '100%', minWidth: 0 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Family Members</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={openFamilyAdd}
                        disabled={familyLoading || familyMembers.length >= 10}
                      >
                        Add
                      </Button>
                    </Box>
                    {familyLoading ? (
                      <Typography variant="body2" color="text.secondary">Loading family members…</Typography>
                    ) : (
                      <Stack spacing={0}>
<Box sx={{ display: 'grid', gridTemplateColumns: '1fr minmax(84px, auto) 88px', gap: 1.5, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                            <Typography variant="caption" color="text.secondary">Name</Typography>
                            <Typography variant="caption" color="text.secondary">Relation</Typography>
                            <Box />
                          </Box>
                          {familyMembers.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No family members yet.</Typography>
                          )}
                          {familyMembers.map((m) => (
                            <Box key={m.id} sx={{ display: 'grid', gridTemplateColumns: '1fr minmax(84px, auto) 88px', gap: 1.5, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" sx={{ fontWeight: m.is_primary ? 700 : 500 }}>
                              {m.name || 'Unnamed'}
                              {m.is_primary ? ' (Self)' : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{formatRelationLabel(m.relation)}</Typography>
                            <Stack direction="row" spacing={0} justifyContent="flex-end">
                              <IconButton size="small" onClick={() => openFamilyEdit(m)} aria-label="Edit"><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => deleteFamilyMember(m)} disabled={!!m.is_primary} aria-label="Delete"><DeleteIcon fontSize="small" /></IconButton>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
                <Card variant="outlined" sx={{ height: '100%', minWidth: 0 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Important Occasions</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={openOccasionAdd}
                        disabled={occasionsLoading || familyMembers.length === 0 || occasions.length >= 3}
                      >
                        Add
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Poshaka benefit: special Archana and blessings on any three important dates (such as birthdays and anniversaries) when you visit the temple in person.
                    </Typography>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={profile.meta?.occasions_email_reminders !== false && profile.meta?.occasions_email_reminders !== 'false'}
                          onChange={(_, v) => saveOccasionsEmailReminder(v)}
                          disabled={occasionsReminderSaving || !displayId}
                          color="primary"
                        />
                      )}
                      label="Email reminders for important occasions"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      When enabled, we email about three days before each saved occasion for current-year Poshaka subscribers. Manage → Alerts and reminders shows a read-only log of those emails.
                    </Typography>
                    {occasionsError && (<Typography variant="body2" color="error" sx={{ mb: 1 }}>{occasionsError}</Typography>)}
                    {occasionsLoading ? (
                      <Typography variant="body2" color="text.secondary">Loading occasions…</Typography>
                    ) : (
                      <Stack spacing={0}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 1, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
                          <Typography variant="caption" color="text.secondary">Member</Typography>
                          <Typography variant="caption" color="text.secondary">Occasion</Typography>
                          <Typography variant="caption" color="text.secondary">Date</Typography>
                        </Box>
                        {occasions.length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No important occasions yet.</Typography>
                        )}
                        {occasions.map((o) => (
                          <Box key={o.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 1, alignItems: 'center', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{o.member?.name || 'Unknown'}</Typography>
                            <Typography variant="body2" color="text.secondary">{formatOccasionTypeLabel(o.occasion_type, o.occasion_label)}</Typography>
                            <Stack direction="row" alignItems="center" spacing={0}>
                              <Typography variant="body2" sx={{ mr: 0.5 }}>
                                {String(o.day).padStart(2, '0')}/{String(o.month).padStart(2, '0')}
                              </Typography>
                              <IconButton size="small" onClick={() => openOccasionEdit(o)} aria-label="Edit"><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" onClick={() => deleteOccasionRow(o)} aria-label="Delete"><DeleteIcon fontSize="small" /></IconButton>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Box>
                  <Typography variant="h6">Donations Overview</Typography>
                  <Typography variant="body2" color="text.secondary">Summary for selected period</Typography>
                </Box>
              }
              action={
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', width: '100%' }}>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => {
                      const cid = displayId || user.customer_id || user.id;
                      navigate(`/post-payment?customer_id=${encodeURIComponent(cid)}`, { state: { user } });
                    }}
                  >
                    Post Payment
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => setPledgesOpen(true)}
                  >
                    Pledges
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => setStmtOpen(true)}
                  >
                    Annual Statements
                  </Button>
                </Stack>
              }
            />
            <CardContent>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}><Stat label="Transactions Count" value={String(lifetimeCount)} help="Transactions (lifetime)" /></Grid>
                <Grid item xs={12} sm={6} md={6}><Stat label="Avg Donation" value={lifetimeAvgFmt} help="Avg per transaction" /></Grid>
                <Grid item xs={12} sm={6} md={6}><Stat label="Total Donations" value={new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(lifetimeTotal || 0)} help="All-time" /></Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: '0 0 auto' }}>
                      <Stat label="This Year" value={new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(ytdTotal ?? 0)} help="YTD completed" />
                    </Box>
                    <Box sx={{ flex: '0 0 auto' }}>
                      <Stat label="Last year" value={new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(lastYearTotal ?? 0)} help="Prior calendar year completed" />
                    </Box>
                    <Box sx={{ flex: '0 0 auto' }}>
                      <Stat label="3 Years" value={new Intl.NumberFormat(undefined, { style: 'currency', currency: (orders?.[0]?.currency || 'USD') }).format(threeYearsTotal ?? 0)} help="Last 3 years completed" />
                    </Box>
                  </Box>
                </Grid>
                
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Orders toolbar: range filters + Year + product search */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  <FilterButton active={range==='all'} onClick={() => { setRange('all'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>All</FilterButton>
                  <FilterButton active={range==='1y'} onClick={() => { setRange('1y'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>Last 12 mon</FilterButton>
                  <FilterButton active={range==='last_year'} onClick={() => { setRange('last_year'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>Last year</FilterButton>
                  <FilterButton active={range==='ytd'} onClick={() => { setRange('ytd'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}>YTD</FilterButton>
                  <FilterButton active={range==='custom'} onClick={() => { setRange('custom'); setSelectedYear(''); setSelectedProduct(null); setProductQuery(''); if(!customStart || !customEnd){ const today=new Date(); const end=today.toISOString().slice(0,10); const s=new Date(today); s.setDate(today.getDate()-30); const start=s.toISOString().slice(0,10); setCustomStart(start); setCustomEnd(end);} }}>Custom</FilterButton>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="year-filter-label">Year</InputLabel>
                    <Select labelId="year-filter-label" label="Year" value={selectedYear}
                      onChange={(e) => { setSelectedYear(e.target.value); setRange(''); setSelectedProduct(null); setProductQuery(''); setOrderPage(1); }}
                    >
                      <MenuItem value=""><em>All</em></MenuItem>
                      {years.map(y => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
                    </Select>
                  </FormControl>
                  {range === 'custom' && (
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">From</Typography>
                        <input
                          type="date"
                          value={customStart}
                          onChange={(e) => setCustomStart(e.target.value)}
                          style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">To</Typography>
                        <input
                          type="date"
                          value={customEnd}
                          onChange={(e) => setCustomEnd(e.target.value)}
                          style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
                        />
                      </Box>
                    </Stack>
                  )}
                </Stack>
                <Box sx={{ minWidth: 320, maxWidth: 480, flex: 1 }}>
                  <Autocomplete
                    size="small"
                    options={productOptions}
                    loading={productLoading}
                    value={selectedProduct}
                    onChange={(_, v) => { setSelectedProduct(v); setOrderPage(1); }}
                    onInputChange={(_, v) => { setProductQuery(v); if ((v||'').trim().length >= 3) loadProductOptions(v); }}
                    getOptionLabel={(o) => o ? `${o.name || ''}${o.sku ? `  ·  ${o.sku}` : ''}` : ''}
                    renderInput={(params) => (
                      <TextField {...params} label="Search product (3+ chars)" placeholder="Type product name" />
                    )}
                    isOptionEqualToValue={(opt, val) => String(opt.id) === String(val?.id)}
                    clearOnBlur={false}
                  />
                </Box>
              </Box>

              {ordersLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                  <Box sx={{ textAlign: 'center', '@keyframes pulse': { '0%': { opacity: 0.4 }, '50%': { opacity: 1 }, '100%': { opacity: 0.4 } } }}>
                    <CircularProgress size={56} sx={{ mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 0.5, animation: 'pulse 1.6s ease-in-out infinite' }}>
                      Retrieving orders...
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Please wait a moment</Typography>
                  </Box>
                </Box>
              )}

              {!ordersLoading && (() => {
                const fmt = (d) => {
                  try { return new Date(d).toLocaleDateString(); } catch { return null; }
                };
                let period = '';
                const after = ordersMeta?.after || null;
                const before = ordersMeta?.before || null;
                if (range === 'all' || (!after && !before)) {
                  period = 'All time';
                } else {
                  const from = after ? fmt(after) : '...';
                  const to = before ? fmt(before) : fmt(new Date().toISOString());
                  period = `${from} to ${to}`;
                }
                return (
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Stack direction="row" spacing={2} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Orders</Typography>
                      <Typography variant="body2" color="text.secondary">{period}</Typography>
                      <Typography variant="body2" color="text.secondary"> ·  Total contributions: {periodTotal}</Typography>
                    </Stack>
                    {(ordersMeta?.total > pageSize) && (
                      <Pagination 
                        count={Math.ceil((ordersMeta?.total || 0) / pageSize)} 
                        page={orderPage} 
                        onChange={(_, p) => setOrderPage(p)} 
                        size="small" 
                      />
                    )}
                  </Stack>
                );
              })()}
              {ordersError && (
                <Typography variant="body2" color="error">{ordersError}</Typography>
              )}
              {!ordersLoading && !ordersError && (
                <Box sx={{ border: '1px dashed #ddd', borderRadius: 1, p: 2 }}>
                  {orders.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No orders found for selected range.</Typography>
                  ) : (
                    (() => {
                      const sorted = [...orders].sort((a,b) => {
                        const da = new Date(a.date_created || a.date_created_gmt || 0).getTime();
                        const db = new Date(b.date_created || b.date_created_gmt || 0).getTime();
                        return db - da; // desc
                      });
                      const buildItemLabel = (li) => {
                        if (!li?.name) return '';
                        let name = li.name.replace(/^Select:\s*/i, '');
                        let variation = li.variation_label || li.variation || '';
                        variation = variation.replace(/^Select:\s*/i, '');
                        if (variation && name.includes(variation)) {
                          variation = '';
                        }
                        const qty = Number(li.quantity || 0);
                        const qtyLabel = qty > 1 ? ` ×${qty}` : '';
                        return variation ? `${name} — ${variation}${qtyLabel}` : `${name}${qtyLabel}`;
                      };
                      // Apply client-side product filtering to guarantee results
                      const lowerQ = (productQuery || '').trim().toLowerCase();
                      const filtered = (() => {
                        if (selectedProduct && selectedProduct.id) {
                          return sorted.filter(o => (o.line_items||[]).some(li => String(li.product_id||'') === String(selectedProduct.id)));
                        } else if (lowerQ.length >= 3) {
                          return sorted.filter(o => (o.line_items||[]).some(li =>
                            String(li.name||'').toLowerCase().includes(lowerQ) || String(li.sku||'').toLowerCase().includes(lowerQ)
                          ));
                        }
                        return sorted;
                      })();
                      return (
                        <Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr 2.6fr 0.8fr 0.9fr', gap: 1, alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Tx #</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Date</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Status</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Product</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>Total</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center' }}>Action</Typography>
                          </Box>
                          {(() => {
                            // Use filtered orders directly (already paginated from server)
                            const rows = filtered;
                            return rows.map((o, idx) => (
                              <Box key={o.id} sx={{ display: 'grid', gridTemplateColumns: '0.8fr 1fr 1fr 2.6fr 0.8fr 0.9fr', gap: 1, alignItems: 'center', py: 0.75, px: 1, borderRadius: 0.5,
                                backgroundColor: idx % 2 === 0 ? 'background.paper' : '#f7f7f7',
                                color: String(o.status||'').toLowerCase()==='pledge-pending' ? '#800000' : 'inherit'
                              }}>
                                <Typography variant="body2" sx={{ color: 'inherit' }}>{o.id}</Typography>
                                <Typography variant="body2" sx={{ color: 'inherit' }}>{new Date(o.date_created || o.date_created_gmt).toLocaleDateString()}</Typography>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize', color: 'inherit' }}>{o.status}</Typography>
                                <Box>
                                  {(Array.isArray(o.line_items) ? o.line_items : []).map((li, liIdx) => (
                                    <Typography key={`${o.id}-li-${li.product_id || 'p'}-${li.variation_id || 'v'}-${liIdx}`} variant="body2" sx={{ color: 'inherit', lineHeight: 1.4 }}>
                                      {buildItemLabel(li)}
                                    </Typography>
                                  ))}
                                </Box>
                                <Typography variant="body2" sx={{ textAlign: 'right', color: 'inherit' }}>${o.total}</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                  <Button size="small" variant="outlined" onClick={() => {
                                    const oid = Number(o.id);
                                    if (!oid) return;
                                    navigate(`/orders/${oid}/edit`, { state: { returnTo: `${location.pathname}${location.search}` } });
                                  }}>Edit</Button>
                                </Box>
                              </Box>
                            ));
                          })()}
                          {(ordersMeta?.total > pageSize) && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                              <Pagination 
                                count={Math.ceil((ordersMeta?.total || 0) / pageSize)} 
                                page={orderPage} 
                                onChange={(_, p) => setOrderPage(p)} 
                                size="small" 
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })()
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Edit Details Dialog */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Edit Devotee Details</Typography>
            <CloseX onClick={() => setEditOpen(false)} />
          </DialogTitle>
          <DialogContent dividers>
            {editError && (<Typography color="error" variant="body2" sx={{ mb: 1 }}>{editError}</Typography>)}
            {/* Personal Details Section */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} gutterBottom>Personal Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <TextField label="First Name" fullWidth size="small" value={editForm.first_name} onChange={handleEditChange('first_name')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Last Name" fullWidth size="small" value={editForm.last_name} onChange={handleEditChange('last_name')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Email" fullWidth size="small" value={email} disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Phone" fullWidth size="small" value={editForm.phone} onChange={handleEditChange('phone')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Spouse" fullWidth size="small" value={editForm.spouse} onChange={handleEditChange('spouse')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Family Members (comma separated)" fullWidth size="small" value={editForm.family_members} onChange={handleEditChange('family_members')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Gothram" fullWidth size="small" value={editForm.gothram} onChange={handleEditChange('gothram')} disabled={editLoading} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            {/* Address Section */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} gutterBottom>Address</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Address 1" fullWidth size="small" value={editForm.address_1} onChange={handleAddressChange('address_1')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Address 2" fullWidth size="small" value={editForm.address_2} onChange={handleAddressChange('address_2')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="City" fullWidth size="small" value={editForm.city} onChange={handleAddressChange('city')} disabled={editLoading} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small" disabled={editLoading}>
                  <InputLabel id="state-label">State</InputLabel>
                  <Select
                    labelId="state-label"
                    label="State"
                    value={editForm.state || ''}
                    onChange={handleAddressChange('state')}
                    renderValue={(val) => (US_STATES.find(s => s.code === val)?.name || val)}
                  >
                    {US_STATES.map(({ code, name }) => (
                      <MenuItem key={code} value={code}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField label="Postal Code" fullWidth size="small" value={editForm.postcode} onChange={handleAddressChange('postcode')} disabled={editLoading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={editLoading}>
                  <InputLabel id="country-label">Country</InputLabel>
                  <Select labelId="country-label" label="Country" value={editForm.country || 'US'} onChange={handleAddressChange('country')}>
                    <MenuItem value="US">United States</MenuItem>
                    <MenuItem value="CA">Canada</MenuItem>
                    <MenuItem value="IN">India</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {editLoading && <CircularProgress size={20} sx={{ mr: 2 }} />}
            <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Devotee Dialog */}
        <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Devotee</DialogTitle>
          <DialogContent dividers>
            {!deleteSuccess ? (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  This will permanently delete devotee <strong>{name}</strong> (ID: {displayId}).
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Deletion is allowed only when the devotee has no WooCommerce orders.
                </Typography>
              </>
            ) : null}
            {deleteSuccess ? <Alert severity="success" sx={{ mb: 1 }}>{deleteSuccess}</Alert> : null}
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </DialogContent>
          <DialogActions>
            {!deleteSuccess ? (
              <>
                <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
                <Button color="error" variant="contained" onClick={handleDeleteDevotee} disabled={deleteLoading}>
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={() => {
                  setDeleteOpen(false);
                  navigate('/devotees');
                }}
              >
                Go to Devotees
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Family Member Add/Edit Dialog */}
        <Dialog open={familyDialogOpen} onClose={() => !familyFormSaving && setFamilyDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{familyForm.id ? 'Edit Family Member' : 'Add Family Member'}</DialogTitle>
          <DialogContent dividers>
            {familyFormError && <Alert severity="error" sx={{ mb: 1 }}>{familyFormError}</Alert>}
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <TextField
                label="Name"
                fullWidth
                size="small"
                value={familyForm.name}
                onChange={(e) => setFamilyForm((f) => ({ ...f, name: e.target.value }))}
                disabled={familyFormSaving}
              />
              <FormControl fullWidth size="small" disabled={familyFormSaving}>
                <InputLabel>Relation</InputLabel>
                <Select
                  label="Relation"
                  value={familyForm.relation}
                  onChange={(e) => setFamilyForm((f) => ({ ...f, relation: e.target.value }))}
                >
                  {RELATION_OPTIONS.map((r) => (
                    <MenuItem key={r} value={r}>{r === 'self' ? 'Self' : r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFamilyDialogOpen(false)} disabled={familyFormSaving}>Cancel</Button>
            <Button variant="contained" onClick={saveFamily} disabled={familyFormSaving}>
              {familyFormSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Occasion Add/Edit Dialog */}
        <Dialog open={occasionDialogOpen} onClose={() => !occasionFormSaving && setOccasionDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{occasionForm.id ? 'Edit Occasion' : 'Add Important Occasion'}</DialogTitle>
          <DialogContent dividers>
            {occasionFormError && <Alert severity="error" sx={{ mb: 1 }}>{occasionFormError}</Alert>}
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <FormControl fullWidth size="small" disabled={occasionFormSaving}>
                <InputLabel>Family Member</InputLabel>
                <Select
                  label="Family Member"
                  value={occasionForm.family_member_id ?? ''}
                  onChange={(e) => setOccasionForm((f) => ({ ...f, family_member_id: e.target.value || null }))}
                >
                  {familyMembers.map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.name || 'Unnamed'}{m.is_primary ? ' (Self)' : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" disabled={occasionFormSaving}>
                <InputLabel>Occasion Type</InputLabel>
                <Select
                  label="Occasion Type"
                  value={occasionForm.occasion_type}
                  onChange={(e) => setOccasionForm((f) => ({ ...f, occasion_type: e.target.value }))}
                >
                  {OCCASION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t === 'marriage_anniversary' ? 'Marriage anniversary' : t === 'birthday' ? 'Birthday' : 'Other'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {occasionForm.occasion_type === 'other' && (
                <TextField
                  label="Occasion label"
                  fullWidth
                  size="small"
                  value={occasionForm.occasion_label}
                  onChange={(e) => setOccasionForm((f) => ({ ...f, occasion_label: e.target.value }))}
                  disabled={occasionFormSaving}
                />
              )}
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 80 }} disabled={occasionFormSaving}>
                  <InputLabel>Month</InputLabel>
                  <Select
                    label="Month"
                    value={occasionForm.month}
                    onChange={(e) => setOccasionForm((f) => ({ ...f, month: Number(e.target.value) }))}
                  >
                    {MONTHS.map((mo) => (
                      <MenuItem key={mo} value={mo}>{new Date(2000, mo - 1, 1).toLocaleString('default', { month: 'short' })}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 80 }} disabled={occasionFormSaving}>
                  <InputLabel>Day</InputLabel>
                  <Select
                    label="Day"
                    value={occasionForm.day}
                    onChange={(e) => setOccasionForm((f) => ({ ...f, day: Number(e.target.value) }))}
                  >
                    {DAYS.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOccasionDialogOpen(false)} disabled={occasionFormSaving}>Cancel</Button>
            <Button variant="contained" onClick={saveOccasion} disabled={occasionFormSaving}>
              {occasionFormSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recent Pledges Modal (Real) */}
        <Dialog open={pledgesOpen} onClose={() => setPledgesOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Recent Pledges</Typography>
            <CloseX onClick={() => setPledgesOpen(false)} />
          </DialogTitle>
          <DialogContent dividers>
            {pledgesLoading && (
              <Typography variant="body2" color="text.secondary">Loading pledges...</Typography>
            )}
            {pledgesError && (
              <Typography variant="body2" color="error">{pledgesError}</Typography>
            )}
            {!pledgesLoading && !pledgesError && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr 1fr 1fr', gap: 1, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Date</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Pledge #</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Item</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, textAlign:'right' }}>Pledged</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, textAlign:'right' }}>Paid</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, textAlign:'right' }}>Balance</Typography>
                    {pledges.map(o => {
                      const items = Array.isArray(o.line_items) ? o.line_items : [];
                      const pledged = Number(o.pledge_total ?? o.total_num ?? 0);
                      const paid = Number(o.paid_total ?? 0);
                      const balance = Number(o.balance_total ?? 0);
                      const dateStr = new Date(o.date_created_gmt || o.date_created).toLocaleDateString();
                      return (
                        <React.Fragment key={o.id}>
                          <Typography variant="body2">{dateStr}</Typography>
                          <Button variant="text" size="small" onClick={() => {
                            setSelectedPledge(o);
                            setPledgeTxns(Array.isArray(o.transactions) ? o.transactions : []);
                          }}>{o.id}</Button>
                          <Box>
                            {items.length === 0 ? (
                              <Typography variant="body2">—</Typography>
                            ) : (
                              items.map((li, idx) => (
                                <Typography key={`${o.id}-pledge-item-${idx}`} variant="body2" sx={{ lineHeight: 1.4 }}>
                                  {li.name || 'Item'}
                                  {li.quantity && Number(li.quantity) > 1 ? ` ×${Number(li.quantity)}` : ''}
                                </Typography>
                              ))
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ textAlign:'right' }}>${pledged.toFixed(2)}</Typography>
                          <Typography variant="body2" sx={{ textAlign:'right' }}>${paid.toFixed(2)}</Typography>
                          <Typography variant="body2" sx={{ textAlign:'right' }} color={balance>0?'warning.main':(balance<0?'error.main':'success.main')}>${balance.toFixed(2)}</Typography>
                          {/* Inline transactions block for the selected pledge */}
                          {selectedPledge?.id === o.id && (
                            <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', my: 1 }}>
                              {pledgeTxns.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No completed payments found for this pledge item.</Typography>
                              ) : (
                                <Box sx={{ width: { xs: '100%', md: '75%' }, border: '1px dashed #ddd', borderRadius: 1, p: 1.5 }}>
                                  <Typography variant="subtitle2" sx={{ mb: 1, textAlign:'center' }}>Transactions for Pledge #{o.id}</Typography>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1, alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Date</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Order #</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Qty</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600, textAlign:'right' }}>Amount</Typography>
                                    {pledgeTxns.map((t,i)=> (
                                      <React.Fragment key={`${t.orderId}-${i}`}>
                                        <Typography variant="body2">{new Date(t.date).toLocaleDateString()}</Typography>
                                        <Typography variant="body2">{t.orderId}</Typography>
                                        <Typography variant="body2">{t.qty}</Typography>
                                        <Typography variant="body2" sx={{ textAlign:'right' }}>${t.amount.toFixed(2)}</Typography>
                                      </React.Fragment>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
          </DialogActions>
        </Dialog>

        {/* Annual Statements Dialog */}
        <Dialog open={stmtOpen} onClose={closeStatementDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Annual Contribution Statement</Typography>
            <CloseX onClick={closeStatementDialog} />
          </DialogTitle>
          <DialogContent dividers>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2">Select Year</Typography>
              <select
                value={stmtYear}
                onChange={(e) => { const y = parseInt(e.target.value, 10); setStmtYear(y); }}
                style={{ height: 32, padding: '4px 8px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)' }}
              >
                {(() => {
                  const items = [];
                  const current = new Date().getFullYear();
                  for (let y = current; y >= 2022; y--) {
                    items.push(<option key={y} value={y}>{y}</option>);
                  }
                  return items;
                })()}
              </select>
            </Stack>

            {stmtLoading && (<Typography variant="body2" color="text.secondary">Loading...</Typography>)}
            {stmtError && (<Typography variant="body2" color="error">{stmtError}</Typography>)}
            {stmtEmailStatus.message && (
              <Alert severity={stmtEmailStatus.type === 'error' ? 'error' : 'success'} sx={{ mb: 1.5 }}>
                {stmtEmailStatus.message}
              </Alert>
            )}
            {!stmtLoading && !stmtError && (
              <Box id="stmt-printable">
                {/* Greeting and advisory note (always shown) */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Namaste {name},
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Thank you for your generous support. This is your contribution statement for the year {stmtYear}. You may use this statement for tax purposes as permitted by IRS guidelines. ICCT provides this statement as a courtesy and is not responsible for any IRS inquiries related to misuse or interpretation of this information.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    With gratitude,
                    <br />
                    India Cultural Center and Temple (ICCT)
                  </Typography>
                </Box>
                {stmtOrders.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No transactions found for {stmtYear}.</Typography>
                ) : (
                  (() => {
                    const rows = [...stmtOrders].sort((a,b) => new Date(b.date_created||b.date_created_gmt) - new Date(a.date_created||a.date_created_gmt));
                    const total = rows.reduce((s,o) => s + (parseFloat(o.total)||0), 0);
                    const currency = rows[0]?.currency || 'USD';
                    const fmt = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(n||0));
                    return (
                      <>
                        {/* Header blocks: Contribution Statement + Devotee and ICCT info side-by-side */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>Contribution Statement</Typography>
                          <Grid container spacing={0} sx={{ columnGap: '50px' }}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>For Year {stmtYear}</Typography>
                              <Typography variant="body2"><strong>Name:</strong> {name}</Typography>
                              <Typography variant="body2"><strong>Email:</strong> {email}</Typography>
                              <Typography variant="body2"><strong>Phone:</strong> {phone}</Typography>
                              {fullAddress && (
                                <Typography variant="body2"><strong>Address:</strong> {fullAddress.split('\n').join(', ')}</Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Statement Provided By</Typography>
                              <Typography variant="body2"><strong>India Cultural Center and Temple (ICCT)</strong></Typography>
                              <Typography variant="body2">12005 Highway 64, Eads, TN 38028</Typography>
                              <Typography variant="body2">Phone: (901) 202-6070  ·  Email: info@icctmemphis.org</Typography>
                              <Typography variant="body2">Website: www.icctmemphis.org  ·  Tax ID: 58-1522313</Typography>
                            </Grid>
                          </Grid>
                        </Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Contributions in {stmtYear}</Typography>
                        <Box>
                          {/* Header row */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: '0.6fr 1fr 1.2fr 2.8fr 0.9fr', gap: 0.75, alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Seq #</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Date</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Status</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>Contribution</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'right' }}>Total</Typography>
                          </Box>
                          {/* Rows with alternating backgrounds */}
                          {rows.map((o, idx) => (
                            <Box key={o.id} sx={{ display: 'grid', gridTemplateColumns: '0.6fr 1fr 1.2fr 2.8fr 0.9fr', gap: 0.75, alignItems: 'center', py: 0.5, px: 0.75, borderRadius: 0.5, backgroundColor: idx % 2 === 0 ? 'background.paper' : '#f7f7f7' }}>
                              <Typography variant="body2">{idx + 1}</Typography>
                              <Typography variant="body2">{new Date(o.date_created || o.date_created_gmt).toLocaleDateString()}</Typography>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{o.status}</Typography>
                              <Box>
                                {(Array.isArray(o.line_items) ? o.line_items : []).map((li, liIdx) => (
                                  <Typography
                                    key={`${o.id}-stmt-item-${liIdx}`}
                                    variant="body2"
                                    sx={{ lineHeight: 1.4 }}
                                  >
                                    {li.name || 'Item'}
                                    {li.quantity && Number(li.quantity) > 1 ? ` ×${Number(li.quantity)}` : ''}
                                  </Typography>
                                ))}
                              </Box>
                              <Typography variant="body2" sx={{ textAlign:'right' }}>{fmt(o.total)}</Typography>
                            </Box>
                          ))}
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" sx={{ textAlign:'right', mb: 2 }}>Total: {fmt(total)}</Typography>
                        {/* Disclaimer */}
                        <Typography variant="caption" color="text.secondary">
                          India Cultural Center and Temple (ICCT) is a registered 501(c)(3) non-profit organization. Donations are tax-deductible as allowed by IRS regulations. Tax ID: 58-1522313
                        </Typography>
                      </>
                    );
                  })()
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                // Print statement: print dialog content
                window.print();
              }}
            >
              Print Contribution Statement
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                try {
                  if (!email) {
                    setStmtEmailStatus({ type: 'error', message: 'Devotee email is missing. Unable to send statement.' });
                    return;
                  }
                  setStmtEmailSending(true);
                  setStmtEmailStatus({ type: '', message: '' });
                  const subject = `Contribution Statement - ${stmtYear}`;
                  const html = buildEmailStatementHtml();
                  await post('/api/statements/email', { to: email, subject, html });
                  setStmtEmailStatus({
                    type: 'success',
                    message: `Contribution statement email has been sent to ${email}.`
                  });
                } catch (e) {
                  setStmtEmailStatus({
                    type: 'error',
                    message: `Failed to email statement: ${e?.response?.data?.error || e?.message || 'Unknown error'}`
                  });
                } finally {
                  setStmtEmailSending(false);
                }
              }}
              disabled={stmtEmailSending || stmtLoading}
            >
              {stmtEmailSending ? 'Sending...' : 'Email Contribution Statement'}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </PageLayout>
  );
}

export default DevoteeDetailPage;
